/**
 * Sort and group games based on sort settings
 * @param {Array} games - Array of game objects
 * @param {Object} sortSettings - Sort configuration object
 * @returns {Object} Object with grouped and sorted games
 */
export const sortAndGroupGames = (games, sortSettings) => {
  if (!games || !sortSettings) return { groups: [], ungroupedGames: games };

  const { groupBy, sortBy, sortDirection, showLeagueLocksGroup } = sortSettings;

  // Separate league locks if needed
  let leagueLockGames = [];
  let regularGames = [];

  if (showLeagueLocksGroup) {
    // Games with any locks go to league locks section
    leagueLockGames = games.filter(game =>
      game.locks && game.locks.length > 0
    );
    regularGames = games.filter(game =>
      !game.locks || game.locks.length === 0
    );
  } else {
    // All games go to regular section
    regularGames = games;
  }

  // Sort regular games
  const sortedRegularGames = sortGames(regularGames, sortBy, sortDirection);

  // Group sorted games
  const groups = groupGames(sortedRegularGames, groupBy, sortBy, sortDirection);

  return {
    leagueLockGames: sortGames(leagueLockGames, sortBy, sortDirection),
    groups,
    ungroupedGames: groupBy === 'none' ? sortedRegularGames : []
  };
};

/**
 * Sort games by specified criteria
 * @param {Array} games - Array of game objects
 * @param {string} sortBy - Sort criteria
 * @param {string} sortDirection - 'asc' or 'desc'
 * @returns {Array} Sorted array of games
 */
const sortGames = (games, sortBy, sortDirection) => {
  return [...games].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'kickoff':
        comparison = sortByKickoffTime(a, b);
        break;
      case 'chronological':
        comparison = sortByChronological(a, b);
        break;
      case 'teamRanking':
        comparison = sortByTeamRanking(a, b);
        break;
      case 'spreadSize':
        comparison = sortBySpreadSize(a, b);
        break;
      case 'totalPoints':
        comparison = sortByTotalPoints(a, b);
        break;
      case 'homeTeam':
        comparison = sortByHomeTeam(a, b);
        break;
      case 'awayTeam':
        comparison = sortByAwayTeam(a, b);
        break;
      default:
        comparison = sortByKickoffTime(a, b);
    }

    return sortDirection === 'desc' ? -comparison : comparison;
  });
};

/**
 * Group games by specified criteria
 * @param {Array} games - Array of game objects
 * @param {string} groupBy - Grouping criteria
 * @param {string} sortBy - Sort criteria (for day grouping logic)
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Array} Array of group objects
 */
const groupGames = (games, groupBy, sortBy, sortDirection) => {
  if (groupBy === 'none') return [];

  const groups = {};

  games.forEach(game => {
    let groupKey = '';

    switch (groupBy) {
      case 'day':
        groupKey = getDayGroup(game.start_date);
        break;
      case 'conference':
        groupKey = getConferenceGroup(game);
        break;
      case 'league':
        groupKey = getLeagueGroup(game);
        break;
      default:
        groupKey = 'Other';
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(game);
  });

  // Sort games within each group by the selected criteria
  Object.keys(groups).forEach(groupKey => {
    groups[groupKey] = sortGames(groups[groupKey], sortBy, sortDirection);
  });

  // Convert to array and sort groups
  return Object.entries(groups)
    .map(([key, games]) => ({ key, games, title: getGroupTitle(key, groupBy) }))
    .sort((a, b) => getGroupSortOrder(a.key, b.key, groupBy, sortBy, a.games, b.games));
};

// Sorting functions
const sortByKickoffTime = (a, b) => {
  // Special handling for kickoff time: active games first, then future, then completed
  const aStatus = getGameStatus(a);
  const bStatus = getGameStatus(b);

  if (aStatus !== bStatus) {
    // Status order: active (0), future (1), completed (2)
    return aStatus - bStatus;
  }

  // Within same status, sort by start time
  return new Date(a.start_date) - new Date(b.start_date);
};

const sortByChronological = (a, b) => {
  return new Date(a.start_date) - new Date(b.start_date);
};

const sortByTeamRanking = (a, b) => {
  const aRank = getHighestTeamRank(a);
  const bRank = getHighestTeamRank(b);

  // Handle unranked teams (null values)
  if (aRank === null && bRank === null) return 0;
  if (aRank === null) return 1; // Unranked teams go last
  if (bRank === null) return -1;

  return aRank - bRank; // Lower rank number = higher ranking
};

const sortBySpreadSize = (a, b) => {
  const aSpread = Math.abs(a.betting?.spread || 0);
  const bSpread = Math.abs(b.betting?.spread || 0);
  return aSpread - bSpread;
};

const sortByTotalPoints = (a, b) => {
  const aTotal = a.betting?.over_under || 0;
  const bTotal = b.betting?.over_under || 0;
  return aTotal - bTotal;
};

const sortByHomeTeam = (a, b) => {
  const aName = a.home_team?.name || '';
  const bName = b.home_team?.name || '';
  return aName.localeCompare(bName);
};

const sortByAwayTeam = (a, b) => {
  const aName = a.away_team?.name || '';
  const bName = b.away_team?.name || '';
  return aName.localeCompare(bName);
};

// Helper functions
const getGameStatus = (game) => {
  const now = new Date();
  const startTime = new Date(game.start_date);
  const endTime = new Date(game.start_date);
  endTime.setHours(endTime.getHours() + 4); // Assume 4 hour game duration

  if (now >= startTime && now <= endTime) return 0; // Active
  if (now < startTime) return 1; // Future
  return 2; // Completed
};

const getHighestTeamRank = (game) => {
  const homeRank = game.home_team?.rank || null;
  const awayRank = game.away_team?.rank || null;

  if (homeRank === null && awayRank === null) return null;
  if (homeRank === null) return awayRank;
  if (awayRank === null) return homeRank;

  return Math.min(homeRank, awayRank); // Lower number = higher ranking
};

const isDayCompleted = (dayKey, games = []) => {
  // Special cases for today/tomorrow
  if (dayKey === 'today' || dayKey === 'tomorrow') return false;

  // Check if all games in this day are completed
  return games.every(game => {
    const status = getGameStatus(game);
    return status === 2; // 2 = completed
  });
};

const getDayGroup = (startDate) => {
  const date = new Date(startDate);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Get user's timezone (browser timezone)
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Get date strings in user's timezone for comparison (YYYY-MM-DD format)
  const getDateString = (d) => {
    const year = d.toLocaleDateString('en-US', { timeZone, year: 'numeric' });
    const month = d.toLocaleDateString('en-US', { timeZone, month: '2-digit' });
    const day = d.toLocaleDateString('en-US', { timeZone, day: '2-digit' });
    return `${year}-${month}-${day}`;
  };

  const gameDateStr = getDateString(date);
  const todayStr = getDateString(today);
  const tomorrowStr = getDateString(tomorrow);

  if (gameDateStr === todayStr) return 'today';
  if (gameDateStr === tomorrowStr) return 'tomorrow';

  return gameDateStr;
};

const getConferenceGroup = (game) => {
  const homeConference = game.home_team?.conference;
  const awayConference = game.away_team?.conference;

  // If either team doesn't have a conference, use the one that does
  if (!homeConference && !awayConference) return 'Unknown';
  if (!homeConference) return awayConference;
  if (!awayConference) return homeConference;

  // If both teams have the same conference, use that conference
  if (homeConference === awayConference) return homeConference;

  // If teams have different conferences, it's a non-conference game
  return 'Non-Conference';
};

const getLeagueGroup = (game) => {
  // Use home team's classification as primary, fallback to away team
  return game.home_team?.classification || game.away_team?.classification || 'Unknown';
};

const getGroupTitle = (key, groupBy) => {
  switch (groupBy) {
    case 'day': {
      switch (key) {
        case 'today': return 'Today';
        case 'tomorrow': return 'Tomorrow';
        default: {
          // Parse YYYY-MM-DD format (already in user's timezone from getDayGroup)
          const [year, month, day] = key.split('-');
          // Create a date object - we'll format it in user's timezone
          // Note: new Date() with YYYY-MM-DD interprets as UTC, so we need to be careful
          // Instead, create date components and format directly
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          // Create a date at noon in the user's timezone to avoid DST issues
          // Then format it back to get the weekday
          const dateStr = `${year}-${month}-${day}`;
          // Use Intl.DateTimeFormat to format the date properly
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timeZone,
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          // Parse the date string and create a date in UTC, then format in user timezone
          // Since the key is YYYY-MM-DD, we can append time and parse
          const utcDate = new Date(`${dateStr}T12:00:00Z`);
          return formatter.format(utcDate);
        }
      }
    }
    case 'conference':
      return key;
    case 'league': {
      if (key === 'fbs') return 'FBS';
      if (key === 'fcs') return 'FCS';
      return key;
    }
    default:
      return key;
  }
};

// Helper function for day group sorting
const getDayGroupSortOrder = (a, b, sortBy, aGames = [], bGames = []) => {
  // Today, Tomorrow, then future days, then completed days
  if (a === 'today') return -1;
  if (b === 'today') return 1;
  if (a === 'tomorrow') return -1;
  if (b === 'tomorrow') return 1;

  // For "chronological" sort (no status sort), use absolute chronological order
  if (sortBy === 'chronological') {
    return a.localeCompare(b);
  }

  // For "kickoff" sort (with status sort), move completed days to bottom
  const aIsCompleted = isDayCompleted(a, aGames);
  const bIsCompleted = isDayCompleted(b, bGames);

  // If one is completed and the other isn't, completed goes last
  if (aIsCompleted && !bIsCompleted) return 1;
  if (!aIsCompleted && bIsCompleted) return -1;

  // If both are completed or both are not completed, sort chronologically
  return a.localeCompare(b);
};

// Helper function for conference group sorting
const getConferenceGroupSortOrder = (a, b) => {
  // Non-Conference first, then alphabetical
  if (a === 'Non-Conference') return -1;
  if (b === 'Non-Conference') return 1;
  return a.localeCompare(b);
};

// Helper function for league group sorting
const getLeagueGroupSortOrder = (a, b) => {
  // FBS first, then FCS, then others
  if (a === 'fbs') return -1;
  if (b === 'fbs') return 1;
  if (a === 'fcs') return -1;
  if (b === 'fcs') return 1;
  return a.localeCompare(b);
};

const getGroupSortOrder = (a, b, groupBy, sortBy, aGames = [], bGames = []) => {
  switch (groupBy) {
    case 'day':
      return getDayGroupSortOrder(a, b, sortBy, aGames, bGames);
    case 'conference':
      return getConferenceGroupSortOrder(a, b);
    case 'league':
      return getLeagueGroupSortOrder(a, b);
    default:
      return a.localeCompare(b);
  }
};

// Export all functions for testing
export {
  sortGames,
  groupGames,
  sortByKickoffTime,
  sortByChronological,
  sortByTeamRanking,
  sortBySpreadSize,
  sortByTotalPoints,
  sortByHomeTeam,
  sortByAwayTeam,
  getGameStatus,
  getHighestTeamRank,
  getDayGroup,
  getConferenceGroup,
  getLeagueGroup,
  getGroupTitle,
  getGroupSortOrder,
  isDayCompleted,
  getDayGroupSortOrder,
  getConferenceGroupSortOrder,
  getLeagueGroupSortOrder
};
